import { toDisposable } from './disposable'

const PENDING_WARN_MS = 10_000

export class ServiceRegistry {
  #services = new Map() // id -> impl
  #waiters = new Map() // id -> Set<resolve>
  #watchers = new Map() // id -> Set<cb>

  provide(id, impl) {
    if (this.#services.has(id)) {
      console.warn(`[omni] service "${id}" provided twice; replacing`)
    }
    this.#services.set(id, impl)
    const waiters = this.#waiters.get(id)
    if (waiters) {
      this.#waiters.delete(id)
      for (const resolve of waiters) resolve(impl)
    }
    for (const cb of this.#watchers.get(id) ?? []) {
      try {
        cb(impl)
      } catch (err) {
        console.error(`[omni] whenAvailable("${id}") callback failed`, err)
      }
    }
    return toDisposable(() => {
      if (this.#services.get(id) === impl) this.#services.delete(id)
    })
  }

  getNow(id) {
    return this.#services.get(id)
  }

  get(id) {
    const existing = this.#services.get(id)
    if (existing !== undefined) return Promise.resolve(existing)
    return new Promise((resolve) => {
      let waiters = this.#waiters.get(id)
      if (!waiters) {
        waiters = new Set()
        this.#waiters.set(id, waiters)
      }
      waiters.add(resolve)
      if (import.meta.env.DEV) {
        const timer = setTimeout(() => {
          if (this.#waiters.get(id)?.has(resolve)) {
            console.warn(
              `[omni] service "${id}" still not provided after ${PENDING_WARN_MS}ms — a plugin is waiting on it. Missing/disabled provider, or a provider that awaits before provide()?`
            )
          }
        }, PENDING_WARN_MS)
        const orig = resolve
        waiters.delete(resolve)
        resolve = (impl) => {
          clearTimeout(timer)
          orig(impl)
        }
        waiters.add(resolve)
      }
    })
  }

  whenAvailable(id, cb) {
    let watchers = this.#watchers.get(id)
    if (!watchers) {
      watchers = new Set()
      this.#watchers.set(id, watchers)
    }
    watchers.add(cb)
    const existing = this.#services.get(id)
    if (existing !== undefined) cb(existing)
    return toDisposable(() => watchers.delete(cb))
  }
}
